import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useUpsertAgreement } from './useAgreements';
import type { Agreement } from './types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  edit?: Agreement | undefined;
}

export function AgreementModal({ open, onOpenChange, edit }: Props) {
  const upsert = useUpsertAgreement();
  const [name, setName] = useState('');
  const [operator, setOperator] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [isActive, setIsActive] = useState(true);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    if (edit) {
      setName(edit.name);
      setOperator(edit.operator ?? '');
      setContactName(edit.contact_name ?? '');
      setContactPhone(edit.contact_phone ?? '');
      setContactEmail(edit.contact_email ?? '');
      setNotes(edit.notes ?? '');
      setIsActive(edit.is_active);
    } else {
      setName('');
      setOperator('');
      setContactName('');
      setContactPhone('');
      setContactEmail('');
      setNotes('');
      setIsActive(true);
    }
  }, [open, edit]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const valid = name.trim().length > 0;

  const handleSubmit = async () => {
    try {
      await upsert.mutateAsync({
        ...(edit?.id ? { id: edit.id } : {}),
        input: {
          name,
          operator,
          contactName,
          contactPhone,
          contactEmail,
          notes,
          isActive,
        },
      });
      onOpenChange(false);
    } catch {
      // toast já mostrou
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-brand text-lg">
            {edit ? 'Editar convênio' : 'Novo convênio'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="ag-name">Nome *</Label>
              <Input
                id="ag-name"
                placeholder="Ex: OdontoPrev"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ag-operator">Operadora</Label>
              <Input
                id="ag-operator"
                placeholder="Ex: Bradesco Saúde"
                value={operator}
                onChange={(e) => setOperator(e.target.value)}
              />
            </div>
          </div>

          <div className="rounded-lg border border-border p-3">
            <div className="mb-2 text-sm font-semibold">Contato</div>
            <div className="space-y-2">
              <Input
                placeholder="Nome do responsável"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  placeholder="Telefone"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                />
                <Input
                  placeholder="E-mail"
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ag-notes">Observações</Label>
            <Textarea
              id="ag-notes"
              rows={2}
              placeholder="Detalhes contratuais, prazos de repasse, etc."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-4 w-4 rounded accent-primary"
            />
            Convênio ativo
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={upsert.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!valid || upsert.isPending}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {upsert.isPending ? (
              <>
                <Loader2 size={14} className="mr-2 animate-spin" />
                Salvando…
              </>
            ) : (
              'Salvar'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
